import {
	Alert,
	Box,
	Button,
	Chip,
	CircularProgress,
	Divider,
	IconButton,
	InputAdornment,
	InputBase,
	List,
	ListItemButton,
	ListItemText,
	Paper,
	Table,
	TableBody,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import queryString from 'query-string';
import { StyledTableCell, StyledTableRow } from 'ui/StyledTable';
import { COLORS } from 'services/constants/colors';
import {
	getDistinctEnumNamesRequest,
	getEnumByNameRequest,
} from 'redux/reducers/emManagement/reducer';
import {
	getDistinctEnumNames,
	getDistinctEnumNamesLoading,
	getEnumsByName,
	getEnumsByNameError,
	getEnumsByNameLoading,
} from 'redux/reducers/emManagement/selectors';
import { humanizeEnumName, matchesSearch } from './utils';
import { Close } from '@mui/icons-material';

/** Keeps the opened enum in the url, so the view can be shared and survives a reload. */
const ENUM_PARAM = 'enum';

export const Enums: FC = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { pathname, search } = useLocation();

	const enumNames = useSelector(getDistinctEnumNames);
	const enumNamesLoading = useSelector(getDistinctEnumNamesLoading);
	const enumsByName = useSelector(getEnumsByName);
	const enumsByNameLoading = useSelector(getEnumsByNameLoading);
	const enumsByNameError = useSelector(getEnumsByNameError);

	const [nameSearch, setNameSearch] = useState('');
	/** Scoped to the enum it was typed for, so switching enums clears the filter without an effect. */
	const [valueSearchState, setValueSearchState] = useState({ enumName: '', text: '' });

	const selectedName = useMemo(() => {
		const param = queryString.parse(search)[ENUM_PARAM];
		return param ? String(param) : '';
	}, [search]);

	useEffect(() => {
		dispatch(getDistinctEnumNamesRequest());
	}, [dispatch]);

	const filteredNames = useMemo(
		() =>
			(enumNames ?? []).filter(
				(name) =>
					matchesSearch(name, nameSearch) || matchesSearch(humanizeEnumName(name), nameSearch),
			),
		[enumNames, nameSearch],
	);

	const selectName = useCallback(
		(name: string) => {
			const params = { ...queryString.parse(search), [ENUM_PARAM]: name };
			navigate(`${pathname}?${queryString.stringify(params)}`, { replace: true });
		},
		[navigate, pathname, search],
	);

	/** Falls back to the first enum in the list so the detail pane is never empty on open. */
	useEffect(() => {
		if (!selectedName && (enumNames?.length ?? 0) > 0) {
			selectName((enumNames as string[])[0]);
		}
	}, [enumNames, selectName, selectedName]);

	/**
	 * Values are cached per name in the store, so an already loaded enum is not refetched. A failed
	 * one is not retried either — otherwise a rejecting enum would be requested on every render.
	 */
	useEffect(() => {
		if (
			selectedName &&
			!enumsByName[selectedName] &&
			!enumsByNameLoading[selectedName] &&
			!enumsByNameError[selectedName]
		) {
			dispatch(getEnumByNameRequest({ enumName: selectedName }));
		}
	}, [dispatch, enumsByName, enumsByNameError, enumsByNameLoading, selectedName]);

	const handleRefresh = useCallback(() => {
		if (selectedName) {
			dispatch(getEnumByNameRequest({ enumName: selectedName }));
		}
	}, [dispatch, selectedName]);

	const valueSearch = valueSearchState.enumName === selectedName ? valueSearchState.text : '';

	const handleValueSearchChange = useCallback(
		(text: string) => setValueSearchState({ enumName: selectedName, text }),
		[selectedName],
	);

	const selectedItems = selectedName ? (enumsByName[selectedName] ?? null) : null;
	const selectedLoading = selectedName ? Boolean(enumsByNameLoading[selectedName]) : false;
	const selectedError = selectedName ? (enumsByNameError[selectedName] ?? '') : '';

	const filteredItems = useMemo(
		() =>
			(selectedItems ?? []).filter(
				(item) =>
					matchesSearch(String(item.value), valueSearch) ||
					matchesSearch(String(item.displayName ?? ''), valueSearch),
			),
		[selectedItems, valueSearch],
	);

	return (
		<Box>
			<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
				<Typography variant="body1" color="primary">
					Resources / Reference Tables / General / <b>Enums</b>
				</Typography>
				<Typography variant="body2" color="text.secondary">
					{enumNames?.length ?? 0} enums
				</Typography>
			</Box>

			<Box sx={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
				<Paper sx={{ width: '320px', flexShrink: 0, p: '8px' }}>
					<Box
						component="div"
						sx={{
							display: 'flex',
							alignItems: 'center',
							width: '300px',
							borderRadius: '12px',
							borderWidth: '1px',
							borderStyle: 'solid',
							borderColor: 'primary.main',
							fontSize: '.9rem',
							p: 0,
							m: 0,
						}}
					>
						{/* <Button
				color="primary"
				sx={{
					p: 0,
					m: 0,
					idTransform: 'none',
					fontWeight: '900',
					fontFamily: 'monospace',
					minWidth: '30px',
					textTransform: 'lowercase',
				}}
				//  onClick={handleClear}
			>
				i
				<InfoDial text="The search will be conducted based on a full match with the EAS order ID or a partial match with the store's order number. All other filters will be reset." />
			</Button>
			<Divider sx={{ height: 18 }} orientation="vertical" /> */}

						<InputBase
							sx={{
								ml: 0,
								flex: 1,
								fontSize: '.9rem',
								p: '2px 4px',
								'& input': {
									padding: '4px 4px',
									fontSize: '.9rem',
									'&::placeholder': {
										fontSize: '.8rem',
									},
								},
							}}
							style={{ padding: 0 }}
							size="small"
							fullWidth
							placeholder={`Enter enum`}
							inputProps={{
								autoComplete: 'new-password',
							}}
							onChange={(event) => setNameSearch(event.target.value)}
							value={nameSearch}
						/>
						<Divider sx={{ height: 18, m: 0.5 }} orientation="vertical" />
						<IconButton
							color="primary"
							sx={{ p: '0 5px 0 0' }}
							aria-label="directions"
							onClick={() => setNameSearch('')}
						>
							<Close fontSize="small" />
						</IconButton>
					</Box>

					{/* <TextField
						fullWidth
						size="small"
						placeholder="Search enum"
						value={nameSearch}
						onChange={(event) => setNameSearch(event.target.value)}
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									<SearchIcon fontSize="small" />
								</InputAdornment>
							),
						}}
					/> */}
					{enumNamesLoading && (
						<Box sx={{ display: 'flex', justifyContent: 'center', p: '16px' }}>
							<CircularProgress size={22} />
						</Box>
					)}
					{!enumNamesLoading && filteredNames.length === 0 && (
						<Typography variant="body2" color="text.secondary" sx={{ p: '12px' }}>
							No enums found
						</Typography>
					)}
					<List dense sx={{ maxHeight: 'calc(100vh - 220px)', overflowY: 'auto', mt: '4px' }}>
						{filteredNames.map((name) => (
							<ListItemButton
								key={name}
								selected={name === selectedName}
								onClick={() => selectName(name)}
								sx={{
									borderRadius: '4px',
									'&.Mui-selected': { backgroundColor: COLORS.BACKGROUND_SECONDARY },
								}}
							>
								<ListItemText
									primary={humanizeEnumName(name)}
									secondary={name}
									primaryTypographyProps={{ fontSize: '.8rem', fontWeight: 'bold' }}
									secondaryTypographyProps={{ fontSize: '.7rem' }}
								/>
								{enumsByNameError[name] ? (
									<Chip size="small" color="error" label="!" sx={{ height: '18px' }} />
								) : (
									enumsByName[name] && (
										<Chip size="small" label={enumsByName[name].length} sx={{ height: '18px' }} />
									)
								)}
							</ListItemButton>
						))}
					</List>
				</Paper>

				<Paper sx={{ flexGrow: 1, p: '8px', minWidth: 0 }}>
					<Box
						sx={{
							display: 'flex',
							justifyContent: 'space-between',
							alignItems: 'center',
							gap: '10px',
							mb: '8px',
						}}
					>
						<Box sx={{ minWidth: 0 }}>
							<Typography variant="subtitle2" color="primary" noWrap>
								{selectedName ? humanizeEnumName(selectedName) : 'Select an enum'}
							</Typography>
							{selectedName && (
								<Typography variant="caption" color="text.secondary">
									{selectedName}
									{selectedItems ? ` · ${selectedItems.length} values` : ''}
								</Typography>
							)}
						</Box>
						<Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
							<Box
								component="div"
								sx={{
									display: 'flex',
									alignItems: 'center',
									width: '300px',
									borderRadius: '12px',
									borderWidth: '1px',
									borderStyle: 'solid',
									borderColor: 'primary.main',
									fontSize: '.9rem',
									p: 0,
									m: 0,
								}}
							>
								{/* <Button
				color="primary"
				sx={{
					p: 0,
					m: 0,
					idTransform: 'none',
					fontWeight: '900',
					fontFamily: 'monospace',
					minWidth: '30px',
					textTransform: 'lowercase',
				}}
				//  onClick={handleClear}
			>
				i
				<InfoDial text="The search will be conducted based on a full match with the EAS order ID or a partial match with the store's order number. All other filters will be reset." />
			</Button>
			<Divider sx={{ height: 18 }} orientation="vertical" /> */}

								<InputBase
									sx={{
										ml: 0,
										flex: 1,
										fontSize: '.9rem',
										p: '2px 4px',
										'& input': {
											padding: '4px 4px',
											fontSize: '.9rem',
											'&::placeholder': {
												fontSize: '.8rem',
											},
										},
									}}
									style={{ padding: 0 }}
									size="small"
									fullWidth
									placeholder={`Enter value`}
									inputProps={{
										autoComplete: 'new-password',
									}}
									onChange={(event) => handleValueSearchChange(event.target.value)}
									value={valueSearch}
								/>
								<Divider sx={{ height: 18, m: 0.5 }} orientation="vertical" />
								<IconButton
									color="primary"
									sx={{ p: '0 5px 0 0' }}
									aria-label="directions"
									onClick={() => handleValueSearchChange('')}
								>
									<Close fontSize="small" />
								</IconButton>
							</Box>

							{/* <TextField
								size="small"
								placeholder="Search value"
								value={valueSearch}
								onChange={(event) => handleValueSearchChange(event.target.value)}
								disabled={!selectedName}
								InputProps={{
									startAdornment: (
										<InputAdornment position="start">
											<SearchIcon fontSize="small" />
										</InputAdornment>
									),
								}}
							/> */}
							<IconButton size="small" onClick={handleRefresh} disabled={!selectedName}>
								<RefreshIcon fontSize="small" />
							</IconButton>
						</Box>
					</Box>

					{selectedLoading && (
						<Box sx={{ display: 'flex', justifyContent: 'center', p: '24px' }}>
							<CircularProgress size={22} />
						</Box>
					)}

					{!selectedLoading && selectedError && (
						<Alert
							severity="error"
							action={
								<Button color="inherit" size="small" onClick={handleRefresh}>
									Retry
								</Button>
							}
						>
							Could not load <b>{selectedName}</b>: {selectedError}
						</Alert>
					)}

					{!selectedLoading && !selectedError && (
						<TableContainer sx={{ maxHeight: 'calc(100vh - 236px)' }}>
							<Table stickyHeader size="small">
								<TableHead>
									<TableRow>
										<StyledTableCell sx={{ width: '30%' }}>Value</StyledTableCell>
										<StyledTableCell>Display Name</StyledTableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{filteredItems.map((item) => (
										<StyledTableRow key={`${selectedName}-${item.value}`}>
											<StyledTableCell>{item.value}</StyledTableCell>
											<StyledTableCell>{item.displayName}</StyledTableCell>
										</StyledTableRow>
									))}
									{filteredItems.length === 0 && (
										<TableRow>
											<StyledTableCell colSpan={2}>
												<Typography variant="body2" color="text.secondary">
													{selectedItems ? 'No values match the search' : 'No values'}
												</Typography>
											</StyledTableCell>
										</TableRow>
									)}
								</TableBody>
							</Table>
						</TableContainer>
					)}
				</Paper>
			</Box>
		</Box>
	);
};
